import { formatISO } from "date-fns";
import { v4 as uuid } from "uuid";
import {
    addCalendarDays,
    isCalendarDateLocked,
    toUTCDateOnly,
} from "@common/helpers/date";
import {
    CreateEliminatingEntryBody,
    EliminatingEntry,
} from "@common/schemas/domains/accounting/eliminatingEntry.schema";

export interface EliminatingEntryLineForm {
    // Stable client-only identity for React list keys — index keys would
    // re-associate row UI state (caret, open combobox) on mid-list deletes.
    clientId: string;
    // Group member entity this line is attributed to. Drives which chart the
    // account picker offers; the account still posts to the overlay chart.
    entityId: number | null;
    accountId: string | null;
    accountCode: number | null;
    debitAmount: number | undefined;
    creditAmount: number | undefined;
    counterpartyId: string | null;
    classIds: string[];
    excludeFrom1099: boolean;
    description: string;
}

export interface EliminatingEntryFormValues {
    date: string;
    reversedAt: string | null;
    memo: string;
    lines: EliminatingEntryLineForm[];
}

const DEFAULT_LINE_COUNT = 20;

function emptyLine(): EliminatingEntryLineForm {
    return {
        clientId: uuid(),
        entityId: null,
        accountId: null,
        accountCode: null,
        debitAmount: undefined,
        creditAmount: undefined,
        counterpartyId: null,
        classIds: [],
        excludeFrom1099: false,
        description: "",
    };
}

function emptyLines(count: number): EliminatingEntryLineForm[] {
    return Array.from({ length: count }, emptyLine);
}

export function appendEmptyLines(
    lines: EliminatingEntryLineForm[],
): EliminatingEntryLineForm[] {
    return [...lines, ...emptyLines(DEFAULT_LINE_COUNT)];
}

export function getMinimumUnlockedDate(
    lockDate: Date | null | undefined,
): string | undefined {
    if (!lockDate) {
        return undefined;
    }
    return addCalendarDays(toUTCDateOnly(lockDate), 1);
}

export function isEliminatingEntryEditLocked({
    storedEntryDate,
    lockDate,
    isLockStateResolved,
}: {
    storedEntryDate?: Date | string;
    lockDate: Date | null | undefined;
    isLockStateResolved: boolean;
}): boolean {
    if (storedEntryDate === undefined) {
        return false;
    }
    if (!isLockStateResolved) {
        return true;
    }
    return isCalendarDateLocked(storedEntryDate, lockDate);
}

// The API stores date-only values; the coerced Date is UTC midnight, so the
// calendar day must be read from the UTC fields — local formatting (formatISO)
// would render the previous day in UTC-negative timezones.
function toUtcDateString(value: Date | string): string {
    const isoString =
        value instanceof Date ? value.toISOString() : String(value);
    return isoString.slice(0, 10);
}

export function initialFormValues(
    entry?: EliminatingEntry,
    initialDate?: string,
): EliminatingEntryFormValues {
    if (!entry) {
        return {
            date:
                initialDate ??
                formatISO(new Date(), { representation: "date" }),
            reversedAt: null,
            memo: "",
            lines: emptyLines(DEFAULT_LINE_COUNT),
        };
    }
    return {
        date: toUtcDateString(entry.date),
        reversedAt: entry.reversedAt ? toUtcDateString(entry.reversedAt) : null,
        memo: entry.memo ?? "",
        lines: appendEmptyLines(
            entry.lines.map((line) => ({
                clientId: uuid(),
                entityId: line.entityId ?? null,
                accountId: line.accountId,
                accountCode: line.accountCode,
                debitAmount: line.debitAmount ?? undefined,
                creditAmount: line.creditAmount ?? undefined,
                counterpartyId: line.counterpartyId ?? null,
                classIds: line.classIds ?? [],
                excludeFrom1099: line.excludeFrom1099 ?? false,
                description: line.description,
            })),
        ),
    };
}

function sumAmounts(lines: EliminatingEntryLineForm[]): {
    debitTotal: number;
    creditTotal: number;
} {
    let debitTotal = 0;
    let creditTotal = 0;
    for (const line of lines) {
        debitTotal += line.debitAmount ?? 0;
        creditTotal += line.creditAmount ?? 0;
    }
    return { debitTotal, creditTotal };
}

export interface FormValidity {
    isBalanced: boolean;
    hasCompleteLines: boolean;
    hasLineErrors: boolean;
    hasBalanceError: boolean;
    isEmpty: boolean;
    canSubmit: boolean;
    debitTotal: number;
    creditTotal: number;
}

// Mirrors the backend line schema: a line must carry a debit or a credit,
// never both.
function hasExactlyOneAmount(line: EliminatingEntryLineForm): boolean {
    return (line.debitAmount ?? 0) > 0 !== (line.creditAmount ?? 0) > 0;
}

// A line the request body will actually carry: account chosen and exactly
// one amount set.
type SubmittableLine = EliminatingEntryLineForm & {
    entityId: number;
    accountId: string;
    accountCode: number;
};

// Validity, the displayed totals, and toRequestBody must all agree on this
// set — otherwise the form can look balanced while the submitted entry is
// not. Typed as a predicate so `.filter` narrows away the nullable fields.
function isSubmittableLine(
    line: EliminatingEntryLineForm,
): line is SubmittableLine {
    return (
        line.entityId !== null &&
        line.accountId !== null &&
        line.accountCode !== null &&
        hasExactlyOneAmount(line)
    );
}

export function getFormValidity(
    values: EliminatingEntryFormValues,
): FormValidity {
    const submittableLines = values.lines.filter(isSubmittableLine);
    const { debitTotal, creditTotal } = sumAmounts(submittableLines);
    const isBalanced =
        debitTotal.toFixed(2) === creditTotal.toFixed(2) && debitTotal > 0;
    const hasDanglingAmounts = values.lines.some(
        (line) =>
            !isSubmittableLine(line) &&
            ((line.debitAmount ?? 0) > 0 || (line.creditAmount ?? 0) > 0),
    );
    // A negative amount is neither a valid nor a dangling amount under the
    // `> 0` checks above, so it would otherwise be silently dropped from the
    // submitted body. Block submission instead so the entry can never post
    // differently from what the user sees.
    const hasNegativeAmounts = values.lines.some(
        (line) => (line.debitAmount ?? 0) < 0 || (line.creditAmount ?? 0) < 0,
    );
    const hasAmounts = values.lines.some(
        (line) =>
            (line.debitAmount ?? 0) !== 0 || (line.creditAmount ?? 0) !== 0,
    );
    const hasIncompleteLines = values.lines.some(
        (line) =>
            (line.accountId !== null ||
                line.entityId !== null ||
                (line.debitAmount ?? 0) !== 0 ||
                (line.creditAmount ?? 0) !== 0) &&
            !isSubmittableLine(line),
    );
    const hasCompleteLines = submittableLines.length >= 2;
    const hasLineErrors =
        hasAmounts &&
        (hasIncompleteLines || hasNegativeAmounts || !hasCompleteLines);
    const hasBalanceError = hasAmounts && !isBalanced;
    return {
        isBalanced,
        hasCompleteLines,
        hasLineErrors,
        hasBalanceError,
        isEmpty: !hasAmounts,
        debitTotal,
        creditTotal,
        canSubmit:
            isBalanced &&
            hasCompleteLines &&
            !hasDanglingAmounts &&
            !hasNegativeAmounts,
    };
}

export function getFormValidationMessage(validity: FormValidity): string {
    if (validity.isEmpty) {
        return "";
    }
    if (validity.hasLineErrors && validity.hasBalanceError) {
        return "Please complete the highlighted fields and make sure the totals match";
    }
    if (validity.hasLineErrors) {
        return "Please complete the highlighted fields";
    }
    if (validity.hasBalanceError) {
        return "Please check that your total debits match total credits";
    }
    return "";
}

export function toRequestBody(
    values: EliminatingEntryFormValues,
): CreateEliminatingEntryBody {
    const trimmedMemo = values.memo.trim();
    return {
        date: values.date,
        reversedAt: values.reversedAt,
        memo: trimmedMemo === "" ? null : trimmedMemo,
        lines: values.lines.filter(isSubmittableLine).map((line) => ({
            entityId: line.entityId,
            accountId: line.accountId,
            accountCode: line.accountCode,
            debitAmount:
                (line.debitAmount ?? 0) > 0 ? (line.debitAmount ?? null) : null,
            creditAmount:
                (line.creditAmount ?? 0) > 0
                    ? (line.creditAmount ?? null)
                    : null,
            counterpartyId: line.counterpartyId,
            classIds: line.classIds,
            excludeFrom1099: line.excludeFrom1099,
            description: line.description,
        })),
    };
}
